import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import useRules from 'hooks/useRules';

// types
import { GuardProps } from 'types/auth';

// ==============================|| AUTH GUARD ||============================== //

/** Rotas de administração que a role "Cliente" nunca deve acessar, mesmo tendo a rule
 * técnica (ex.: customers.read, que também cobre "ver o próprio cadastro"). Mesma
 * exclusão do menu lateral (ver Navigation/index.tsx) — aqui é o bloqueio de verdade,
 * o menu só evita mostrar o link.
 * `/clients`, `/clients/new` NÃO estão mais bloqueados aqui (17/08): um cliente B2B pode
 * cadastrar os próprios clientes (B2C) — o backend já escopa a listagem/criação pra só
 * ele ver/criar o que é dele (ver computeCustomerScope em customers.service.ts), então
 * abrir a rota não vaza dados de outros clientes B2B. `/clients/:id/edit` continua
 * bloqueado: a role "Cliente" não tem a rule customers.update. */
const EXTERNAL_CLIENT_BLOCKED = [/^\/clients\/[^/]+\/edit$/, /^\/company$/, /^\/billing$/, /^\/taxes$/];

export default function AuthGuard({ children }: GuardProps) {
  const { isLoggedIn, user } = useAuth();
  const { isExternalClient } = useRules();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/', {
        state: {
          from: location.pathname
        },
        replace: true
      });
      return;
    }

    // Login provisionado automaticamente com CPF/CNPJ como senha inicial — trava em
    // /account (aba "Alterar Senha", ver pages/account/index.tsx) até trocar a senha.
    if (user?.mustChangePassword && location.pathname !== '/account') {
      navigate('/account', { replace: true });
      return;
    }

    // Cliente (role "Cliente") nunca acessa telas de administração, mesmo tendo a rule
    // técnica (ex.: customers.read cobre também "ver o próprio cadastro").
    if (isExternalClient && EXTERNAL_CLIENT_BLOCKED.some((re) => re.test(location.pathname))) {
      navigate('/orders', { replace: true });
      return;
    }

    const rules = user?.rules ?? [];
    const isAdmin = rules.includes('administrator');
    if (isAdmin) return;

    const path = location.pathname;
    /** Rotas do painel permitidas a qualquer usuário logado (sem regra específica). */
    const allowedWithoutRule = [
      /^\/account$/,
      /^\/devices$/,
      /^\/blocks$/,
    ];
    if (allowedWithoutRule.some((re) => re.test(path))) return;

    const requiredByPath: Array<[RegExp, string]> = [
      [/^\/orders$/, 'orders.read'],
      [/^\/orders\/new$/, 'orders.create'],
      [/^\/orders\/[^/]+\/edit$/, 'orders.update'],
      [/^\/orders\/[^/]+\/timeline$/, 'orders.read'],
      [/^\/products$/, 'products.read'],
      [/^\/products\/new$/, 'products.create'],
      [/^\/users$/, 'users.read'],
      [/^\/roles$/, 'roles.read'],
      [/^\/departments$/, 'departments.read'],
      [/^\/company$/, 'company.read'],
      [/^\/clients$/, 'customers.read'],
      [/^\/clients\/new$/, 'customers.create'],
      [/^\/clients\/[^/]+\/edit$/, 'customers.update'],
      [/^\/clients\/[^/]+$/, 'customers.read'],
      [/^\/contracts$/, 'projects-management.contracts.read'],
      [/^\/contracts\/new$/, 'projects-management.contracts.create'],
      [/^\/contracts\/[^/]+\/edit$/, 'projects-management.contracts.update'],
      [/^\/contracts\/templates$/, 'projects-management.contracts.templates.read'],
      [/^\/contracts\/[^/]+$/, 'projects-management.contracts.read'],
      [/^\/billing$/, 'orders.read'],
      [/^\/taxes$/, 'taxes.read'],
      [/^\/logistics$/, 'stock.read'],
      [/^\/warehouse-map$/, 'stock.read'],
      [/^\/stock-check$/, 'stock.read'],
      [/^\/floor-workers$/, 'stock.read']
    ];
    const match = requiredByPath.find(([re]) => re.test(path));
    if (!match) {
      navigate('/orders', { replace: true });
      return;
    }
    const requiredRule = match[1];
    if (!rules.includes(requiredRule)) {
      navigate('/orders', { replace: true });
    }
  }, [isLoggedIn, user, isExternalClient, navigate, location]);

  return children;
}
